import { GCS_ORIGIN, toProxiedImageUrl } from "@repo/core";
import { appResourceOrigin } from "./portfolio-host";

// Rewrites a GCS storage URL to a relative /cdn/ path.
// Works in both dev (Vite proxy) and production (web worker at same origin).
// On the marketing-domain portfolio host the path is absolute to the app
// origin, which serves /cdn/ (EPD-10104).
// Non-GCS URLs and null/undefined pass through unchanged.
//
// Pass `width` (CSS px of the largest slot the image renders into) to have the
// edge resize it. Omitting it is safe: the worker caps every image at 1600px
// wide, which is what keeps multi-megapixel source PNGs off the wire.
export function proxyImageUrl(
  url: string | null | undefined,
  width?: number,
): string | undefined {
  return toProxiedImageUrl(url, appResourceOrigin(), width) ?? undefined;
}

/**
 * A `srcset` of edge-resized variants (EPD-12468), one `<url> <w>w` candidate
 * per width, for a GCS-stored image. Pass rungs of the worker's width ladder
 * (64/128/256/512/1024/1600): it snaps any other width up to the next rung, so
 * an off-ladder descriptor would misstate the served width. Undefined for
 * non-GCS URLs (social CDNs), which the proxy can't resize, so the browser
 * just uses `src`.
 */
export function proxyImageSrcSet(
  url: string | null | undefined,
  widths: readonly number[],
): string | undefined {
  if (!url?.startsWith(GCS_ORIGIN) || widths.length === 0) return undefined;
  return widths
    .map((width) => `${proxyImageUrl(url, width)} ${width}w`)
    .join(", ");
}
